import type {
  DevelopmentContentDuration,
  DevelopmentContentLastUpdated,
  DevelopmentContentSort,
  FilterState,
} from '../types';
import { useCallback, useReducer } from 'react';
import { SEARCH_MAX_PAGE_SIZE } from '../constants';

type FilterActionType =
  | 'SET_SEARCH'
  | 'SET_SORT'
  | 'SET_TOPIC'
  | 'SET_PROVIDER'
  | 'SET_DURATION'
  | 'SET_LANGUAGE'
  | 'SET_FILTERS'
  | 'SET_PAGE'
  | 'SET_LAST_UPDATED';

type FilterAction = {
  type: FilterActionType;
  state?: FilterState;
  filters?: FilterState;
  searchKeyword?: string;
  sort?: DevelopmentContentSort;
  topic?: string[];
  provider?: string[];
  duration?: DevelopmentContentDuration[];
  language?: string[];
  limit?: number;
  page?: number;
  lastUpdated?: DevelopmentContentLastUpdated;
};

const { APP_CONSTANTS } = window;

export const initialState: FilterState = {
  limit: SEARCH_MAX_PAGE_SIZE, // For now, page limit will just be a hard-coded value as user cannot change it in UI
  sort: 'POPULARITY',
  searchKeyword: '',
  language: [],
  topic: [],
  duration: [],
  provider: [],
  page: 0,
  lastUpdated: APP_CONSTANTS.GO1_SEARCH_LAST_UPDATED_OPTION.ALL_TIME,
};

const setFilters = (state: FilterState, newState: FilterState) => ({
  ...state,
  ...newState,
});

const setSort = (state: FilterState, sort: DevelopmentContentSort) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  sort,
});

const setSearch = (state: FilterState, searchKeyword: string) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  searchKeyword,
});

const setLanguage = (state: FilterState, language: string[]) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  language,
});

const setTopic = (state: FilterState, topic: string[]) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  topic,
});

const setDuration = (state: FilterState, duration: DevelopmentContentDuration[]) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  duration,
});

const setLastUpdated = (state: FilterState, lastUpdated: DevelopmentContentLastUpdated) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  lastUpdated,
});

const setProvider = (state: FilterState, provider: string[]) => ({
  ...state,
  page: 0, // We need to reset our pagination whenever any Filter is applied, so we always show the first page of results
  provider,
});

const setPage = (state: FilterState, page: number) => ({
  ...state,
  page,
});

const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'SET_FILTERS':
      return setFilters(state, action.filters);
    case 'SET_SORT':
      return setSort(state, action.sort);
    case 'SET_SEARCH':
      return setSearch(state, action.searchKeyword);
    case 'SET_LANGUAGE':
      return setLanguage(state, action.language);
    case 'SET_TOPIC':
      return setTopic(state, action.topic);
    case 'SET_DURATION':
      return setDuration(state, action.duration);
    case 'SET_LAST_UPDATED':
      return setLastUpdated(state, action.lastUpdated);
    case 'SET_PROVIDER':
      return setProvider(state, action.provider);
    case 'SET_PAGE':
      return setPage(state, action.page);
    default:
      return state;
  }
};

export const useFilterState = () => {
  const [state, dispatch] = useReducer(filterReducer, initialState);

  const filterActions = {
    setFilters: useCallback((newFilters: FilterState) => dispatch({ type: 'SET_FILTERS', filters: newFilters }), []),
    setSort: useCallback((sort: DevelopmentContentSort) => dispatch({ type: 'SET_SORT', sort }), []),
    setSearchKeyword: useCallback((searchKeyword: string) => dispatch({ type: 'SET_SEARCH', searchKeyword }), []),
    setLanguage: useCallback((language: string[]) => dispatch({ type: 'SET_LANGUAGE', language }), []),
    setTopic: useCallback((topic: string[]) => dispatch({ type: 'SET_TOPIC', topic }), []),
    setDuration: useCallback(
      (duration: DevelopmentContentDuration[]) => dispatch({ type: 'SET_DURATION', duration }),
      [],
    ),
    setLastUpdated: useCallback(
      (lastUpdated: DevelopmentContentLastUpdated) => dispatch({ type: 'SET_LAST_UPDATED', lastUpdated }),
      [],
    ),
    setProvider: useCallback((provider: string[]) => dispatch({ type: 'SET_PROVIDER', provider }), []),
    setPage: useCallback((page: number) => dispatch({ type: 'SET_PAGE', page }), []),
  };

  return {
    filterState: state,
    filterActions,
  };
};
